import { getAuthRequestHeaders } from "@qw/auth";
import { type ReactNode, useState } from "react";
import { getFilePreviewKind, type PartialFileUpload } from "../utils";
import { Badge } from "./badge";
import { Button } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./dialog";
import { FilePreviewFrame } from "./file-preview-frame";
import { getFilePreviewNavigation } from "./file-preview-nav";
import { Icon } from "./icon";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { type ExtractedResult, ViewPdfWebViewer } from "./view-pdf-webviewer";

export interface FilePreviewDialogProps<T extends PartialFileUpload> {
  extractedResults?: ExtractedResult[];
  file?: T;
  files: T[];
  getFileDetails?: (file: T) => string | null;
  /** A heading to group the file menu by. Files sharing a heading should sit next to each other in `files`. */
  getFileGroup?: (file: T) => string;
  getFileType?: (file: T) => string | null;
  headers?: HeadersInit;
  navButtons?: ReactNode;
  setSelectedFile: (file: T | undefined) => void;
}

const FILL_STYLE = { height: "100%" };

const groupByHeading = <T,>(files: T[], getHeading?: (file: T) => string) =>
  files.reduce<{ heading: string; files: T[] }[]>((groups, file) => {
    const heading = getHeading?.(file) ?? "";
    const last = groups.at(-1);

    if (last?.heading === heading) {
      last.files.push(file);
    } else {
      groups.push({ heading, files: [file] });
    }
    return groups;
  }, []);

const matchesSearch = (_value: string, search: string, keywords: string[] = []) =>
  keywords.join(" ").toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0;

export const FilePreviewDialog = <T extends PartialFileUpload>({
  extractedResults,
  file,
  files,
  getFileDetails,
  getFileGroup,
  getFileType,
  headers,
  navButtons = null,
  setSelectedFile,
}: FilePreviewDialogProps<T>) => {
  const [isFileMenuOpen, setIsFileMenuOpen] = useState(false);

  if (!file) {
    return null;
  }

  const resolvedHeaders = headers ?? getAuthRequestHeaders();
  const previewKind = getFilePreviewKind(file.mimeType);
  const hasExtractedResults = extractedResults && extractedResults.length > 0;
  const { close, download, goToNext, goToPrevious, hasNextFile, hasPreviousFile, index } = getFilePreviewNavigation(
    files,
    file,
    setSelectedFile
  );
  const hasFileList = files.length > 1 && index >= 0;
  const details = getFileDetails?.(file);
  const fileGroups = groupByHeading(files, getFileGroup);
  const typeChip = (candidate: T) => {
    const type = getFileType?.(candidate);

    return (
      type && (
        <Badge variant="secondary" type="square" size="2xs" className="shrink-0">
          {type}
        </Badge>
      )
    );
  };

  return (
    <Dialog open={!!file} onOpenChange={() => setSelectedFile(undefined)}>
      <DialogContent
        showCloseButton={false}
        className="flex flex-col gap-0 h-dvh max-h-none max-w-none overflow-hidden p-0 rounded-none shadow-none sm:rounded-none w-dvw"
      >
        <header className="border-b flex gap-2 h-12 items-center px-3 shrink-0">
          <DialogTitle className="min-w-0 text-sm" title={file.filename}>
            {hasFileList ? (
              <Popover modal open={isFileMenuOpen} onOpenChange={setIsFileMenuOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="max-w-full">
                    <span className="truncate">{file.filename}</span>
                    {typeChip(file)}
                    <Icon icon="keyboard_arrow_down" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="max-w-[calc(100vw-2rem)] p-0 w-md">
                  <Command defaultValue={file.id} filter={matchesSearch}>
                    <CommandInput placeholder="Search files" />
                    <CommandList className="border-t max-h-[calc(80dvh-3rem)]">
                      <CommandEmpty>No files match</CommandEmpty>
                      {fileGroups.map(({ heading, files: groupFiles }, index) => (
                        <CommandGroup key={`${index}-${heading}`} heading={heading || undefined}>
                          {groupFiles.map((candidate) => (
                            <CommandItem
                              key={candidate.id}
                              value={candidate.id}
                              keywords={[candidate.filename, getFileType?.(candidate) ?? ""]}
                              title={candidate.filename}
                              onSelect={() => {
                                setSelectedFile(candidate);
                                setIsFileMenuOpen(false);
                              }}
                            >
                              <span className="flex justify-center shrink-0 w-3.5">
                                {candidate.id === file.id && <Icon icon="circle" className="filled text-[8px]" />}
                              </span>
                              <span className="flex-1 truncate">{candidate.filename}</span>
                              {typeChip(candidate)}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <span className="flex gap-3 items-center">
                <span className="truncate">{file.filename}</span>
                {typeChip(file)}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">Preview of {file.filename}</DialogDescription>
          {details && <span className="text-muted-foreground text-sm truncate whitespace-nowrap">{details}</span>}
          <div className="flex gap-2 items-center ml-auto">
            {navButtons}
            <Button variant="ghost" size="sm" display="icon" aria-label="Download" onClick={download}>
              <Icon icon="download" />
            </Button>
            <Button variant="ghost" size="sm" display="icon" aria-label="Close" onClick={close}>
              <Icon icon="close" />
            </Button>
          </div>
        </header>
        <div className="flex flex-1 items-center justify-center min-h-0 relative">
          {previewKind === "pdf" && hasExtractedResults ? (
            <ViewPdfWebViewer
              headers={resolvedHeaders}
              file={file}
              extractedResults={extractedResults}
              style={FILL_STYLE}
            />
          ) : (
            <FilePreviewFrame headers={resolvedHeaders} file={file} style={FILL_STYLE} />
          )}
          {hasFileList && (
            <nav
              aria-label="Files"
              className="absolute bg-popover bottom-2 flex gap-2 items-center left-1/2 outline outline-background-contrast p-1 rounded-full shadow-xl -translate-x-1/2"
            >
              <Button
                variant="outline"
                size="sm"
                display="icon"
                className="rounded-full"
                aria-label="Previous file"
                onClick={goToPrevious}
                disabled={!hasPreviousFile}
              >
                <Icon icon="chevron_left" />
              </Button>
              <span className="px-1 tabular-nums text-muted-foreground text-sm">
                {index + 1} of {files.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                display="icon"
                className="rounded-full"
                aria-label="Next file"
                onClick={goToNext}
                disabled={!hasNextFile}
              >
                <Icon icon="chevron_right" />
              </Button>
            </nav>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
